import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { sitesAPI, enhancedMonitoringAPI } from '../services/api';
import { Site, MonitoringLog, OptimizationReport } from '../types';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ChartBarIcon,
  CogIcon,
} from '@heroicons/react/24/outline';

const SiteDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [site, setSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [monitoringData, setMonitoringData] = useState<any>(null);
  const [performanceData, setPerformanceData] = useState<any>(null);
  const [securityData, setSecurityData] = useState<any>(null);
  const [seoData, setSeoData] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState('');
  const [apiCredentials, setApiCredentials] = useState<any>(null);
  const [generatingApiKey, setGeneratingApiKey] = useState(false);

  useEffect(() => {
    if (id) {
      fetchSiteDetails();
    }
  }, [id]);

  const fetchSiteDetails = async () => {
    try {
      const response = await sitesAPI.getSite(id!);
      setSite(response.data.site);
      
      // Fetch API credentials if they exist
      if (response.data.site.api_key) {
        setApiCredentials({
          api_key: response.data.site.api_key,
          site_id: response.data.site.id
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch site details');
    } finally {
      setLoading(false);
    }
  };

  const triggerComprehensiveCheck = async () => {
    setActionLoading('monitoring');
    try {
      const response = await enhancedMonitoringAPI.comprehensiveMonitor(id!);
      setMonitoringData(response.data.data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to trigger comprehensive check');
    } finally {
      setActionLoading('');
    }
  };

  const runPerformanceAudit = async () => {
    setActionLoading('performance');
    try {
      const response = await enhancedMonitoringAPI.performanceAudit(id!);
      setPerformanceData(response.data.data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to run performance audit');
    } finally {
      setActionLoading('');
    }
  };

  const runSecurityScan = async () => {
    setActionLoading('security');
    try {
      const response = await enhancedMonitoringAPI.securityScan(id!);
      setSecurityData(response.data.data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to run security scan');
    } finally {
      setActionLoading('');
    }
  };

  const runSeoAnalysis = async () => {
    setActionLoading('seo');
    try {
      const response = await enhancedMonitoringAPI.seoAnalysis(id!);
      setSeoData(response.data.data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to run SEO analysis');
    } finally {
      setActionLoading('');
    }
  };

  const generateApiKey = async () => {
    setGeneratingApiKey(true);
    try {
      const response = await fetch(`/api/sites/${id}/api-key`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate API key');
      }
      
      const data = await response.json();
      setApiCredentials({
        api_key: data.api_key,
        site_id: data.site_id
      });
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to generate API key');
    } finally {
      setGeneratingApiKey(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!site) {
    return (
      <div className="text-center">
        <p className="text-gray-500">Site not found</p>
        <Link to="/sites" className="btn-primary mt-4">
          Back to Sites
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Link
            to="/sites"
            className="text-gray-400 hover:text-gray-600"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{site.name}</h1>
            <p className="text-sm text-gray-500">{site.url}</p>
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={triggerComprehensiveCheck}
            disabled={actionLoading === 'monitoring'}
            className="btn-secondary"
          >
            <CheckCircleIcon className="mr-2 h-4 w-4" />
            {actionLoading === 'monitoring' ? 'Checking...' : 'Check Now'}
          </button>
          <button
            onClick={runPerformanceAudit}
            disabled={actionLoading === 'performance'}
            className="btn-primary"
          >
            <ChartBarIcon className="mr-2 h-4 w-4" />
            {actionLoading === 'performance' ? 'Running...' : 'Run Performance Audit'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {['overview', 'monitoring', 'optimization', 'security', 'seo', 'api-credentials'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab === 'api-credentials' ? 'API Credentials' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="card">
        {activeTab === 'overview' && (
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Site Overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1 text-sm text-gray-900">{site.status}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Monitoring</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {site.monitoring_enabled ? 'Enabled' : 'Disabled'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Created</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(site.created_at).toLocaleDateString()}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(site.updated_at).toLocaleDateString()}
                </dd>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'monitoring' && (
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Monitoring Data</h3>
              <button
                onClick={triggerComprehensiveCheck}
                disabled={actionLoading === 'monitoring'}
                className="btn-secondary text-sm"
              >
                {actionLoading === 'monitoring' ? 'Checking...' : 'Run Check'}
              </button>
            </div>
            
            {monitoringData ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd className={`text-lg font-semibold ${monitoringData.status === 'online' ? 'text-green-600' : 'text-red-600'}`}>
                      {monitoringData.status}
                    </dd>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <dt className="text-sm font-medium text-gray-500">Response Time</dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {monitoringData.responseTime}ms
                    </dd>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <dt className="text-sm font-medium text-gray-500">Last Check</dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {new Date(monitoringData.timestamp).toLocaleString()}
                    </dd>
                  </div>
                </div>
                
                {monitoringData.checks && (
                  <div className="mt-6">
                    <h4 className="text-md font-medium text-gray-900 mb-3">Detailed Checks</h4>
                    <div className="space-y-3">
                      {Object.entries(monitoringData.checks).map(([key, check]: [string, any]) => (
                        <div key={key} className="border rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h5 className="font-medium text-gray-900 capitalize">{key.replace('_', ' ')}</h5>
                              <p className="text-sm text-gray-600 mt-1">
                                Status: <span className={check.success ? 'text-green-600' : 'text-red-600'}>
                                  {check.success ? 'Passed' : 'Failed'}
                                </span>
                              </p>
                            </div>
                            <div className="text-right">
                              {check.response_time && (
                                <p className="text-sm text-gray-500">{check.response_time}ms</p>
                              )}
                              {check.status_code && (
                                <p className="text-sm text-gray-500">Status: {check.status_code}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500">No monitoring data available. Click "Run Check" to get started.</p>
            )}
          </div>
        )}

        {activeTab === 'optimization' && (
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Performance Reports</h3>
              <button
                onClick={runPerformanceAudit}
                disabled={actionLoading === 'performance'}
                className="btn-secondary text-sm"
              >
                {actionLoading === 'performance' ? 'Running...' : 'Run Audit'}
              </button>
            </div>
            
            {performanceData ? (
              <div className="space-y-4">
                {performanceData.auditData && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <dt className="text-sm font-medium text-gray-500">Performance</dt>
                      <dd className="text-lg font-semibold text-gray-900">
                        {performanceData.auditData.performance_score}/100
                      </dd>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <dt className="text-sm font-medium text-gray-500">Accessibility</dt>
                      <dd className="text-lg font-semibold text-gray-900">
                        {performanceData.auditData.accessibility_score}/100
                      </dd>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <dt className="text-sm font-medium text-gray-500">Best Practices</dt>
                      <dd className="text-lg font-semibold text-gray-900">
                        {performanceData.auditData.best_practices_score}/100
                      </dd>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <dt className="text-sm font-medium text-gray-500">SEO</dt>
                      <dd className="text-lg font-semibold text-gray-900">
                        {performanceData.auditData.seo_score}/100
                      </dd>
                    </div>
                  </div>
                )}
                
                {performanceData.recommendations && performanceData.recommendations.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-md font-medium text-gray-900 mb-3">Recommendations</h4>
                    <div className="space-y-3">
                      {performanceData.recommendations.map((rec: any, index: number) => (
                        <div key={index} className="border rounded-lg p-4">
                          <h5 className="font-medium text-gray-900">{rec.title}</h5>
                          <p className="text-sm text-gray-600 mt-1">{rec.description}</p>
                          {rec.potential_savings && (
                            <p className="text-sm text-blue-600 mt-1">
                              Potential savings: {rec.potential_savings}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500">No performance data available. Click "Run Audit" to get started.</p>
            )}
          </div>
        )}

        {activeTab === 'security' && (
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Security Scans</h3>
              <button
                onClick={runSecurityScan}
                disabled={actionLoading === 'security'}
                className="btn-secondary text-sm"
              >
                {actionLoading === 'security' ? 'Scanning...' : 'Run Scan'}
              </button>
            </div>
            
            {securityData ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <dt className="text-sm font-medium text-gray-500">Malware Status</dt>
                    <dd className={`text-lg font-semibold ${securityData.malware ? 'text-red-600' : 'text-green-600'}`}>
                      {securityData.malware ? 'Detected' : 'Clean'}
                    </dd>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <dt className="text-sm font-medium text-gray-500">Vulnerabilities</dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {securityData.vulnerabilities?.length || 0}
                    </dd>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <dt className="text-sm font-medium text-gray-500">Security Score</dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {securityData.securityScore}/100
                    </dd>
                  </div>
                </div>
                
                {securityData.vulnerabilities && securityData.vulnerabilities.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-md font-medium text-gray-900 mb-3">Vulnerabilities Found</h4>
                    <div className="space-y-3">
                      {securityData.vulnerabilities.map((vuln: any, index: number) => (
                        <div key={index} className="border rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <h5 className="font-medium text-gray-900">{vuln.type}</h5>
                              <p className="text-sm text-gray-600 mt-1">{vuln.description}</p>
                            </div>
                            <span className={`px-2 py-1 text-xs rounded ${
                              vuln.severity === 'high' ? 'bg-red-100 text-red-800' :
                              vuln.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {vuln.severity}
                            </span>
                          </div>
                          {vuln.details && (
                            <div className="mt-3 p-3 bg-gray-50 rounded text-sm">
                              {vuln.details.scriptLocation && (
                                <div className="mb-2">
                                  <strong>Location:</strong> {vuln.details.scriptLocation}
                                </div>
                              )}
                              {vuln.details.resourceUrl && (
                                <div className="mb-2">
                                  <strong>Resource:</strong> 
                                  <span className="break-all ml-1">{vuln.details.resourceUrl}</span>
                                </div>
                              )}
                              {vuln.details.elementType && (
                                <div className="mb-2">
                                  <strong>Element:</strong> {vuln.details.elementType}
                                </div>
                              )}
                              {vuln.details.suspiciousPatterns && (
                                <div className="mb-2">
                                  <strong>Suspicious Patterns:</strong> {vuln.details.suspiciousPatterns.join(', ')}
                                </div>
                              )}
                              {vuln.details.recommendation && (
                                <div className="mt-2 p-2 bg-red-50 rounded text-red-800 text-sm">
                                  <strong>How to fix:</strong> {vuln.details.recommendation}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500">No security data available. Click "Run Scan" to get started.</p>
            )}
          </div>
        )}

        {activeTab === 'seo' && (
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">SEO Analysis</h3>
              <button
                onClick={runSeoAnalysis}
                disabled={actionLoading === 'seo'}
                className="btn-secondary text-sm"
              >
                {actionLoading === 'seo' ? 'Analyzing...' : 'Run Analysis'}
              </button>
            </div>
            
            {seoData ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <dt className="text-sm font-medium text-gray-500">SEO Score</dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {seoData.seoScore}/100
                    </dd>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <dt className="text-sm font-medium text-gray-500">Broken Links</dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {seoData.brokenLinks?.length || 0}
                    </dd>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <dt className="text-sm font-medium text-gray-500">Missing Meta Tags</dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {seoData.missingMetaTags?.length || 0}
                    </dd>
                  </div>
                </div>
                
                {seoData.recommendations && seoData.recommendations.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-md font-medium text-gray-900 mb-3">SEO Recommendations</h4>
                    <div className="space-y-3">
                      {seoData.recommendations.map((rec: any, index: number) => (
                        <div key={index} className="border rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <h5 className="font-medium text-gray-900">{rec.title}</h5>
                              <p className="text-sm text-gray-600 mt-1">{rec.description}</p>
                            </div>
                            <span className={`px-2 py-1 text-xs rounded ${
                              rec.priority === 'high' ? 'bg-red-100 text-red-800' :
                              rec.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {rec.priority}
                            </span>
                          </div>
                          {rec.details && (
                            <div className="mt-3 p-3 bg-gray-50 rounded text-sm">
                              {rec.details.affectedImages && (
                                <div className="mb-2">
                                  <strong>Affected Images ({rec.details.totalCount}):</strong>
                                  <ul className="mt-1 space-y-1">
                                    {rec.details.affectedImages.slice(0, 5).map((img: any, i: number) => (
                                      <li key={i} className="text-gray-600 break-all">
                                        • {img.src}
                                      </li>
                                    ))}
                                    {rec.details.totalCount > 5 && (
                                      <li className="text-gray-500">... and {rec.details.totalCount - 5} more</li>
                                    )}
                                  </ul>
                                </div>
                              )}
                              {rec.details.brokenLinks && (
                                <div className="mb-2">
                                  <strong>Broken Links ({rec.details.totalCount}):</strong>
                                  <ul className="mt-1 space-y-1">
                                    {rec.details.brokenLinks.slice(0, 5).map((link: any, i: number) => (
                                      <li key={i} className="text-gray-600 break-all">
                                        • <span className="font-medium">{link.text}</span> → {link.url}
                                        <span className="text-red-600 text-xs ml-2">({link.status})</span>
                                      </li>
                                    ))}
                                    {rec.details.totalCount > 5 && (
                                      <li className="text-gray-500">... and {rec.details.totalCount - 5} more</li>
                                    )}
                                  </ul>
                                </div>
                              )}
                              {rec.details.recommendation && (
                                <div className="mt-2 p-2 bg-blue-50 rounded text-blue-800 text-sm">
                                  <strong>How to fix:</strong> {rec.details.recommendation}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {seoData.brokenLinks && seoData.brokenLinks.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-md font-medium text-gray-900 mb-3">Broken Links</h4>
                    <div className="space-y-2">
                      {seoData.brokenLinks.slice(0, 10).map((link: any, index: number) => (
                        <div key={index} className="flex justify-between items-center p-3 bg-red-50 rounded">
                          <span className="text-sm text-gray-900">{link.url}</span>
                          <span className="text-sm text-red-600">Status: {link.status}</span>
                        </div>
                      ))}
                      {seoData.brokenLinks.length > 10 && (
                        <p className="text-sm text-gray-500">And {seoData.brokenLinks.length - 10} more...</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500">No SEO data available. Click "Run Analysis" to get started.</p>
            )}
          </div>
        )}

        {activeTab === 'api-credentials' && (
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium text-gray-900">API Credentials</h3>
              <p className="text-sm text-gray-500">Use these credentials to connect the WordPress plugin</p>
            </div>

            {apiCredentials ? (
              <div className="space-y-6">
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <CheckCircleIcon className="h-5 w-5 text-green-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-green-800">
                        API credentials are ready!
                      </p>
                      <p className="text-sm text-green-700 mt-1">
                        Copy these credentials to your WordPress plugin configuration.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      API Key
                    </label>
                    <div className="flex">
                      <input
                        type="password"
                        readOnly
                        value={apiCredentials.api_key}
                        className="flex-1 block w-full rounded-l-md border-gray-300 bg-gray-50 px-3 py-2 text-sm"
                      />
                      <button
                        onClick={() => copyToClipboard(apiCredentials.api_key)}
                        className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-500 text-sm hover:bg-gray-100"
                      >
                        Copy
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Site ID
                    </label>
                    <div className="flex">
                      <input
                        type="text"
                        readOnly
                        value={apiCredentials.site_id}
                        className="flex-1 block w-full rounded-l-md border-gray-300 bg-gray-50 px-3 py-2 text-sm"
                      />
                      <button
                        onClick={() => copyToClipboard(apiCredentials.site_id)}
                        className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-500 text-sm hover:bg-gray-100"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <h4 className="text-sm font-medium text-blue-800 mb-2">WordPress Plugin Setup</h4>
                  <ol className="text-sm text-blue-700 space-y-1">
                    <li>1. Download the WordPress plugin from the main dashboard</li>
                    <li>2. Install and activate the plugin in your WordPress admin</li>
                    <li>3. Go to "SaaS Monitor" in your WordPress admin menu</li>
                    <li>4. Enter the API Key and Site ID above</li>
                    <li>5. Click "Save Settings" and test the connection</li>
                  </ol>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100">
                  <CogIcon className="h-6 w-6 text-gray-600" />
                </div>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No API credentials yet</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Generate API credentials to connect your WordPress plugin
                </p>
                <div className="mt-6">
                  <button
                    onClick={generateApiKey}
                    disabled={generatingApiKey}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    {generatingApiKey ? 'Generating...' : 'Generate API Credentials'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SiteDetails;